import { useMutation } from "@apollo/client";
import { TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { useState } from "react";

export const DISMISS_CERS_REJECTION_FINDING = gql(`
  mutation DismissCersRejectionFindingFromDrawer($id: ID!, $reason: String!) {
    dismissCersRejectionFinding(id: $id, reason: $reason) {
      id
      status
      resolutionType
      note
      resolvedAt
      history {
        id
        eventType
        status
        resolutionType
        note
        actorName
        createdAt
      }
    }
  }
`);

type Props = {
  open: boolean;
  findingId: string;
  onClose: () => void;
  /** Called after a successful dismissal (dialog closes itself first). */
  onDone: () => void;
};

/** Dismiss a finding as a false positive → IGNORED. The reason is the audit record. */
export function DismissDialog({ open, findingId, onClose, onDone }: Props) {
  const [reason, setReason] = useState("");
  const alerts = useAlerts();

  const [dismiss, { loading }] = useMutation(DISMISS_CERS_REJECTION_FINDING, {
    refetchQueries: ["StaffCersRejectionFindings", "CersFindingDetail"],
    // Hold the dialog's loading state until the drawer + grid actually show
    // the new status — without this the dialog closes ~1-2s before the chip
    // flips, which reads as "nothing happened".
    awaitRefetchQueries: true,
  });

  const handleClose = () => {
    setReason("");
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await dismiss({ variables: { id: findingId, reason: reason.trim() } });
      alerts.success("Finding dismissed.");
      handleClose();
      onDone();
    } catch (e) {
      alerts.error("Failed to dismiss the finding.", e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 560, maxWidth: "90vw" } } }}
      title="Dismiss finding"
      description="Dismiss this rejection as not real or not actionable. The reason is recorded in the finding's history."
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading,
        loadingText: "Dismissing...",
        hideStartIcon: true,
        saveText: "Dismiss finding",
        disabled: loading || !reason.trim(),
      }}
      cancelButtonProps={{ disabled: loading }}
    >
      <TextField
        label="Reason (required)"
        required
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        size="small"
        multiline
        minRows={2}
        fullWidth
        autoFocus
        sx={{ mt: 1 }}
      />
    </Dialog>
  );
}
